import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { getProduct } from "../services/api";
import { useSettings } from "../context/SettingsContext";
import { useCurrency } from "../context/CurrencyContext";
import { Helmet } from "react-helmet-async";
import { getImageUrl } from "../utils/imageHelper";

const WhatsAppIcon = () => (
  <svg className="wa-icon-svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

export default function ProductDetail() {
  const { id } = useParams();
  const settings = useSettings();
  const { formatPrice, currency } = useCurrency();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await getProduct(id);
        setProduct(data);
      } catch (err) {
        console.error("Failed to load product", err);
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const orderWA = (p) => {
    const waNumber = settings.whatsapp_number || "6282111307477";
    const productUrl = window.location.href;
    const priceDisplay = formatPrice(p.price);
    
    const message = `Halo ${settings.site_name || 'Admin'}, saya tertarik untuk memesan produk ini:\n\n*${p.name}*\nHarga: ${priceDisplay}\n\nLink Produk: ${productUrl}\n\nSilakan isi data pemesanan:\nNama: \nAlamat: \n\nMohon informasi lebih lanjut mengenai cara pemesanannya. Terima kasih.`;
    window.open(`https://wa.me/${waNumber}?text=${encodeURIComponent(message)}`);
  };

  if (loading) return <div className="container" style={{ padding: '100px 0', textAlign: 'center' }}>Memuat informasi produk...</div>;
  if (!product) return <div className="container" style={{ padding: '100px 0', textAlign: 'center' }}>Produk tidak ditemukan.</div>;

  const imageUrl = getImageUrl(product.image, 'https://via.placeholder.com/600x600?text=No+Image');

  const pageTitle = `${product.name} - ${settings.site_name || "HandyCraft Bali"}`;
  const pageDesc = product.description?.substring(0, 160) || `Beli ${product.name} otentik Bali hanya di ${settings.site_name}.`;

  return (
    <div className="container" style={{ paddingTop: '50px', paddingBottom: '80px' }}>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:image" content={imageUrl} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content={imageUrl} />
      </Helmet>

      <Link to="/products" style={{ color: 'var(--primary)', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '30px' }}>
        <span>←</span> Kembali ke Koleksi
      </Link>

      <div className="product-detail-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '60px', alignItems: 'start' }}>
        <div style={{ borderRadius: '30px', overflow: 'hidden', boxShadow: 'var(--shadow)', border: '1px solid var(--border)' }}>
          <img src={imageUrl} alt={product.name} style={{ width: '100%', height: 'auto', display: 'block' }} />
        </div>

        <div>
          <p className="product-category" style={{ fontSize: '1rem' }}>{product.category_name || "Authentic Bali"}</p>
          <h1 className="slide-title" style={{ color: 'var(--secondary)', fontSize: 'clamp(2rem, 5vw, 3rem)', margin: '15px 0' }}>{product.name}</h1>
          <p className="product-price" style={{ fontSize: '2rem', marginBottom: '30px', display: 'flex', alignItems: 'baseline', gap: '15px' }}>
            {formatPrice(product.price)}
            {currency === 'USD' && (
              <span style={{ fontSize: '1.2rem', color: '#888', fontWeight: 'normal' }}>
                (~ {new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(product.price)})
              </span>
            )}
          </p>
          
          <div style={{ padding: '30px', background: 'white', borderRadius: '24px', marginBottom: '40px', border: '1px solid var(--border)' }}>
            <h4 style={{ marginBottom: '15px' }}>Deskripsi Produk</h4>
            <p style={{ color: 'var(--text-light)', lineHeight: '1.8' }}>{product.description || "Tidak ada deskripsi untuk produk ini."}</p>
          </div>

          <div style={{ display: 'flex', gap: '15px' }}>
            <button className="btn btn-primary wa-bounce" onClick={() => orderWA(product)} style={{ flex: 1, padding: '1.2rem', gap: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <WhatsAppIcon />
              Pesan Sekarang via WhatsApp
            </button>
          </div>

          <div style={{ marginTop: '30px', display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9rem', color: 'var(--text-light)' }}>
              <span style={{ color: 'var(--primary)' }}>✓</span> Stok Terjamin
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9rem', color: 'var(--text-light)' }}>
              <span style={{ color: 'var(--primary)' }}>✓</span> Kirim ke Seluruh Dunia
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9rem', color: 'var(--text-light)' }}>
              <span style={{ color: 'var(--primary)' }}>✓</span> 100% Buatan Tangan
            </div>
          </div>

          <div style={{ marginTop: '40px', padding: '25px', background: 'rgba(197, 160, 89, 0.05)', borderRadius: '20px', border: '1px dashed var(--primary)' }}>
            <h5 style={{ color: 'var(--primary)', marginBottom: '10px' }}>Cara Pemesanan:</h5>
             <ol style={{ fontSize: '0.9rem', color: 'var(--text-light)', paddingLeft: '20px', lineHeight: '1.6' }}>
               <li>Klik tombol <b>Pesan via WhatsApp</b> di atas.</li>
               <li>Kirim pesan yang telah terisi otomatis (berisi detail produk).</li>
               <li>Admin kami akan segera membalas untuk konfirmasi stok dan biaya kirim.</li>
               <li>Pembayaran dilakukan via Transfer Bank setelah konfirmasi.</li>
             </ol>
          </div>
        </div>
      </div>
    </div>
  );
}
